import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { StructuralAnalysisHistoryEntryView } from "@/features/ai/ai.types";
import { cn } from "@/lib/utils";

type StructuralAnalysisHistoryListProps = {
  activeRunId: string | null;
  history: StructuralAnalysisHistoryEntryView[];
  onOpenRun: (runId: string) => void;
};

export function StructuralAnalysisHistoryList({
  activeRunId,
  history,
  onOpenRun,
}: StructuralAnalysisHistoryListProps) {
  const { t } = useTranslation();

  if (history.length === 0) {
    return (
      <div className="border-y border-dashed border-ink/10 py-6 text-sm text-muted-foreground">
        {t("story.ai.structural.history.empty")}
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#ece2d6] border-y border-[#ece2d6]">
      {history.map((entry) => {
        const active = entry.runId === activeRunId;

        return (
          <article
            className={cn(
              "space-y-3 py-4 transition",
              active ? "text-foreground" : "hover:text-clay",
            )}
            data-testid="structural-analysis-history-entry"
            key={entry.runId}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  <History className="h-3.5 w-3.5" />
                  <span>{entry.scopeLabel}</span>
                </div>
                <h4 className="text-base font-semibold text-foreground">
                  {new Date(entry.createdAt).toLocaleString()}
                </h4>
              </div>
              <Button
                aria-label={t("story.ai.structural.history.openAriaLabel", {
                  scopeLabel: entry.scopeLabel,
                })}
                onClick={() => onOpenRun(entry.runId)}
                size="sm"
                type="button"
                variant={active ? "default" : "ghost"}
              >
                {active
                  ? t("story.ai.structural.history.openNow")
                  : t("story.ai.structural.history.openRun")}
              </Button>
            </div>

            <p className="text-sm leading-6 text-muted-foreground">
              {entry.previewText}
            </p>

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                {t("story.ai.structural.history.chapterCount", {
                  count: entry.chapterCount,
                })}
              </span>
              <span>|</span>
              <span>
                {t("story.ai.structural.history.creditsUsed", {
                  count: entry.creditsUsed,
                })}
              </span>
            </div>
          </article>
        );
      })}
    </div>
  );
}
